// Canonical (language-independent) option keys stored in DB.
export const PHYS_OPTIONS = {
  height: ["short", "average", "tall"],
  build: ["slim", "athletic", "average", "curvy", "plus"],
  hair: ["blonde", "brown", "black", "red", "grey", "other"],
  style: ["casual", "classic", "edgy", "elegant", "sporty"],
};

export const QUESTION_META = [
  { id: "q1", category: "values", type: "choice" },
  { id: "q2", category: "values", type: "choice" },
  { id: "q3", category: "values", type: "choice" },
  { id: "q4", category: "emotional", type: "choice" },
  { id: "q5", category: "emotional", type: "choice" },
  { id: "q6", category: "emotional", type: "choice" },
  { id: "q7", category: "daily", type: "choice" },
  { id: "q8", category: "daily", type: "choice" },
  { id: "q9", category: "daily", type: "choice" },
  { id: "q10", category: "depth", type: "text" },
  { id: "q11", category: "depth", type: "text" },
  { id: "q12", category: "depth", type: "choice" },
];

export const appStrings = {
  en: {
    verify: {
      title: "Check your inbox",
      subtitle: "Enter the 6-digit code we sent to {email} to verify your account.",
      placeholder: "6-digit code",
      verify: "Verify",
      verifying: "Verifying...",
      resend: "Resend code",
      resendIn: "Resend in {s}s",
      resent: "A new code is on its way 🤍",
      logout: "Use a different account",
    },
    onboarding: {
      title: "Let's find your people",
      subtitle: "Twelve honest questions. No right answers — just you. This is how Reveal matches on what actually matters.",
      start: "Begin",
      progress: "Question {n} of {total}",
      next: "Continue",
      back: "Back",
      finish: "Finish",
      submitting: "Saving...",
      textPlaceholder: "1–2 sentences...",
      required: "Please answer to continue",
      categories: {
        values: { name: "Values & Life Vision", tag: "What you're building toward" },
        emotional: { name: "Emotional Style", tag: "How you love, fight, and heal" },
        daily: { name: "Daily Life & Energy", tag: "Lifestyle compatibility" },
        depth: { name: "Depth & Self-Awareness", tag: "How well you know yourself" },
      },
    },
    questions: {
      q1: { text: "What does a good life look like to you in 10 years?", options: ["Settled somewhere I call home, with people I love around me", "Still exploring — new places, new chapters", "Building something that outlasts me (family, career, legacy)", "I'm still figuring that out, honestly"] },
      q2: { text: "When it comes to commitment, you are:", options: ["All in from the moment I choose someone", "I need time to feel safe before I fully open up", "I love deeply but I need my independence too", "I'm still learning what commitment means for me"] },
      q3: { text: "What role does family play in your life?", options: ["Central — they're my foundation", "Important, but I've built my own chosen family too", "Complicated — it's something I'm working through", "I'm focused on building my own family now"] },
      q4: { text: "When something hurts you in a relationship, you tend to:", options: ["Say it directly — I'd rather talk than simmer", "Pull back first, then come back when I'm ready", "Minimize it so there's no conflict", "Write it out or process it before I can speak it"] },
      q5: { text: "How do you know you feel safe with someone?", options: ["I can be quiet with them and it doesn't feel awkward", "They've shown up for me when it wasn't convenient", "I can say the ugly truth and they don't leave", "I stop performing and just... exist"] },
      q6: { text: "After a real argument, you need:", options: ["Space to breathe before reconnecting", "Immediate reassurance that we're okay", "To understand what happened before I can move forward", "Physical closeness — a hug fixes more than words for me"] },
      q7: { text: "Your ideal Sunday looks like:", options: ["Slow mornings, good food, nowhere to be", "Out in nature or doing something active", "Surrounded by friends and people I love", "Creating something — cooking, writing, making"] },
      q8: { text: "You recharge by:", options: ["Being alone — I need quiet to come back to myself", "Being with one person I trust completely", "Being around people — energy feeds me", "It depends on the season I'm in"] },
      q9: { text: "When it comes to ambition, you are:", options: ["Deeply driven — my goals are a core part of who I am", "Ambitious but I never let it cost me my relationships", "I care more about depth than achievement", "I'm in a season of figuring out what I actually want"] },
      q10: { text: "The thing most people don't see about you right away is:" },
      q11: { text: "What's something you used to believe about love that you no longer do?" },
      q12: { text: "What are you genuinely looking for right now — be honest:", options: ["A real relationship with a real future", "To meet someone meaningful, without pressure", "To heal and grow alongside someone", "I don't know yet — and I'm okay with that"] },
    },
    profileExtra: {
      ageRange: "Age range you're open to",
      to: "to",
      plus: "65+",
      physicalTitle: "Physical attraction",
      physicalSub: "Optional, weighted lightly (10%). Helps us fine-tune your matches.",
      attributes: "About you",
      preferences: "You're drawn to (pick any)",
      preview: "Preview my profile",
      previewTitle: "How others will see you",
      close: "Close",
      lookingForAges: "Open to ages {min}–{max}",
      dims: {
        height: { label: "Height", options: { short: "Short", average: "Average", tall: "Tall" } },
        build: { label: "Build", options: { slim: "Slim", athletic: "Athletic", average: "Average", curvy: "Curvy", plus: "Plus-size" } },
        hair: { label: "Hair", options: { blonde: "Blonde", brown: "Brown", black: "Black", red: "Red", grey: "Grey", other: "Other" } },
        style: { label: "Style", options: { casual: "Casual", classic: "Classic", edgy: "Edgy", elegant: "Elegant", sporty: "Sporty" } },
      },
    },
  },

  es: {
    verify: {
      title: "Revisa tu bandeja de entrada",
      subtitle: "Introduce el código de 6 dígitos que enviamos a {email} para verificar tu cuenta.",
      placeholder: "Código de 6 dígitos",
      verify: "Verificar",
      verifying: "Verificando...",
      resend: "Reenviar código",
      resendIn: "Reenviar en {s}s",
      resent: "Un nuevo código está en camino 🤍",
      logout: "Usar otra cuenta",
    },
    onboarding: {
      title: "Encontremos a tu gente",
      subtitle: "Doce preguntas honestas. No hay respuestas correctas — solo tú. Así conecta Reveal con lo que de verdad importa.",
      start: "Empezar",
      progress: "Pregunta {n} de {total}",
      next: "Continuar",
      back: "Atrás",
      finish: "Finalizar",
      submitting: "Guardando...",
      textPlaceholder: "1–2 frases...",
      required: "Responde para continuar",
      categories: {
        values: { name: "Valores y visión de vida", tag: "Hacia dónde construyes" },
        emotional: { name: "Estilo emocional", tag: "Cómo amas, discutes y sanas" },
        daily: { name: "Vida diaria y energía", tag: "Compatibilidad de estilo de vida" },
        depth: { name: "Profundidad y autoconocimiento", tag: "Qué tan bien te conoces" },
      },
    },
    questions: {
      q1: { text: "¿Cómo se ve una buena vida para ti dentro de 10 años?", options: ["Asentado en un lugar que llamo hogar, rodeado de mis seres queridos", "Aún explorando — nuevos lugares, nuevos capítulos", "Construyendo algo que me trascienda (familia, carrera, legado)", "Sinceramente, todavía lo estoy descubriendo"] },
      q2: { text: "En cuanto al compromiso, tú eres:", options: ["Todo o nada desde el momento en que elijo a alguien", "Necesito tiempo para sentirme seguro antes de abrirme del todo", "Amo profundamente pero también necesito mi independencia", "Aún estoy aprendiendo qué significa el compromiso para mí"] },
      q3: { text: "¿Qué papel juega la familia en tu vida?", options: ["Central — son mi base", "Importante, pero también he creado mi propia familia elegida", "Complicado — es algo que estoy trabajando", "Estoy enfocado en construir mi propia familia ahora"] },
      q4: { text: "Cuando algo te duele en una relación, sueles:", options: ["Decirlo directamente — prefiero hablar que guardarlo", "Retirarme primero y volver cuando estoy listo", "Minimizarlo para que no haya conflicto", "Escribirlo o procesarlo antes de poder decirlo"] },
      q5: { text: "¿Cómo sabes que te sientes seguro con alguien?", options: ["Puedo estar en silencio con esa persona y no se siente incómodo", "Ha estado ahí para mí cuando no le convenía", "Puedo decir la verdad más fea y no se va", "Dejo de actuar y simplemente... existo"] },
      q6: { text: "Después de una discusión de verdad, necesitas:", options: ["Espacio para respirar antes de reconectar", "Reafirmación inmediata de que estamos bien", "Entender qué pasó antes de poder avanzar", "Cercanía física — un abrazo arregla más que las palabras para mí"] },
      q7: { text: "Tu domingo ideal se ve así:", options: ["Mañanas lentas, buena comida, sin planes", "Al aire libre o haciendo algo activo", "Rodeado de amigos y personas que amo", "Creando algo — cocinar, escribir, hacer"] },
      q8: { text: "Recargas energía:", options: ["Estando solo — necesito silencio para volver a mí", "Con una persona en la que confío por completo", "Rodeado de gente — la energía me alimenta", "Depende de la etapa en la que esté"] },
      q9: { text: "En cuanto a la ambición, tú eres:", options: ["Muy motivado — mis metas son parte esencial de quién soy", "Ambicioso pero nunca dejo que cueste mis relaciones", "Me importa más la profundidad que el logro", "Estoy en una etapa de descubrir qué quiero realmente"] },
      q10: { text: "Lo que la mayoría no ve de ti a primera vista es:" },
      q11: { text: "¿Qué solías creer sobre el amor que ya no crees?" },
      q12: { text: "¿Qué buscas realmente ahora mismo — sé honesto:", options: ["Una relación real con un futuro real", "Conocer a alguien significativo, sin presión", "Sanar y crecer junto a alguien", "Aún no lo sé — y está bien"] },
    },
    profileExtra: {
      ageRange: "Rango de edad que te interesa",
      to: "a",
      plus: "65+",
      physicalTitle: "Atracción física",
      physicalSub: "Opcional, con poco peso (10%). Nos ayuda a afinar tus conexiones.",
      attributes: "Sobre ti",
      preferences: "Te atrae (elige las que quieras)",
      preview: "Previsualizar mi perfil",
      previewTitle: "Cómo te verán los demás",
      close: "Cerrar",
      lookingForAges: "Abierto a edades {min}–{max}",
      dims: {
        height: { label: "Altura", options: { short: "Bajo", average: "Media", tall: "Alto" } },
        build: { label: "Complexión", options: { slim: "Delgada", athletic: "Atlética", average: "Media", curvy: "Con curvas", plus: "Talla grande" } },
        hair: { label: "Cabello", options: { blonde: "Rubio", brown: "Castaño", black: "Negro", red: "Pelirrojo", grey: "Canoso", other: "Otro" } },
        style: { label: "Estilo", options: { casual: "Casual", classic: "Clásico", edgy: "Atrevido", elegant: "Elegante", sporty: "Deportivo" } },
      },
    },
  },

  fr: {
    verify: {
      title: "Vérifiez votre boîte mail",
      subtitle: "Saisissez le code à 6 chiffres envoyé à {email} pour vérifier votre compte.",
      placeholder: "Code à 6 chiffres",
      verify: "Vérifier",
      verifying: "Vérification...",
      resend: "Renvoyer le code",
      resendIn: "Renvoyer dans {s}s",
      resent: "Un nouveau code arrive 🤍",
      logout: "Utiliser un autre compte",
    },
    onboarding: {
      title: "Trouvons vos personnes",
      subtitle: "Douze questions sincères. Pas de bonnes réponses — juste vous. C'est ainsi que Reveal associe sur ce qui compte vraiment.",
      start: "Commencer",
      progress: "Question {n} sur {total}",
      next: "Continuer",
      back: "Retour",
      finish: "Terminer",
      submitting: "Enregistrement...",
      textPlaceholder: "1–2 phrases...",
      required: "Répondez pour continuer",
      categories: {
        values: { name: "Valeurs et vision de vie", tag: "Ce que vous construisez" },
        emotional: { name: "Style émotionnel", tag: "Comment vous aimez, vous vous disputez et guérissez" },
        daily: { name: "Vie quotidienne et énergie", tag: "Compatibilité de mode de vie" },
        depth: { name: "Profondeur et conscience de soi", tag: "À quel point vous vous connaissez" },
      },
    },
    questions: {
      q1: { text: "À quoi ressemble une belle vie pour vous dans 10 ans ?", options: ["Installé dans un endroit que j'appelle chez moi, entouré de ceux que j'aime", "Toujours en exploration — nouveaux lieux, nouveaux chapitres", "Construire quelque chose qui me survivra (famille, carrière, héritage)", "Honnêtement, je suis encore en train de le découvrir"] },
      q2: { text: "En matière d'engagement, vous êtes :", options: ["À fond dès l'instant où je choisis quelqu'un", "J'ai besoin de temps pour me sentir en sécurité avant de m'ouvrir", "J'aime profondément mais j'ai aussi besoin de mon indépendance", "J'apprends encore ce que l'engagement signifie pour moi"] },
      q3: { text: "Quel rôle la famille joue-t-elle dans votre vie ?", options: ["Central — c'est mon socle", "Important, mais j'ai aussi bâti ma propre famille de cœur", "Compliqué — c'est quelque chose sur lequel je travaille", "Je me concentre sur la construction de ma propre famille maintenant"] },
      q4: { text: "Quand quelque chose vous blesse dans une relation, vous avez tendance à :", options: ["Le dire directement — je préfère parler que ruminer", "Me retirer d'abord, puis revenir quand je suis prêt", "Le minimiser pour éviter le conflit", "L'écrire ou le digérer avant de pouvoir en parler"] },
      q5: { text: "Comment savez-vous que vous vous sentez en sécurité avec quelqu'un ?", options: ["Je peux rester silencieux avec cette personne sans gêne", "Elle a été là pour moi quand ce n'était pas pratique", "Je peux dire la vérité la plus dure et elle ne part pas", "J'arrête de jouer un rôle et j'existe, tout simplement"] },
      q6: { text: "Après une vraie dispute, vous avez besoin de :", options: ["De l'espace pour respirer avant de renouer", "Être rassuré tout de suite que tout va bien", "Comprendre ce qui s'est passé avant d'avancer", "De la proximité physique — un câlin répare plus que les mots"] },
      q7: { text: "Votre dimanche idéal ressemble à :", options: ["Matinées tranquilles, bonne nourriture, rien à faire", "En pleine nature ou en activité", "Entouré d'amis et de ceux que j'aime", "Créer quelque chose — cuisiner, écrire, fabriquer"] },
      q8: { text: "Vous rechargez vos batteries en :", options: ["Étant seul — j'ai besoin de calme pour me retrouver", "Étant avec une personne en qui j'ai totalement confiance", "Étant entouré de gens — l'énergie me nourrit", "Ça dépend de la période où je suis"] },
      q9: { text: "En matière d'ambition, vous êtes :", options: ["Très motivé — mes objectifs font partie de qui je suis", "Ambitieux mais jamais au prix de mes relations", "J'accorde plus d'importance à la profondeur qu'à la réussite", "Je suis dans une période où je cherche ce que je veux vraiment"] },
      q10: { text: "Ce que la plupart des gens ne voient pas de vous au premier abord :" },
      q11: { text: "Qu'aviez-vous l'habitude de croire sur l'amour et que vous ne croyez plus ?" },
      q12: { text: "Que cherchez-vous vraiment en ce moment — soyez honnête :", options: ["Une vraie relation avec un vrai avenir", "Rencontrer quelqu'un d'important, sans pression", "Guérir et grandir aux côtés de quelqu'un", "Je ne sais pas encore — et c'est très bien"] },
    },
    profileExtra: {
      ageRange: "Tranche d'âge qui vous intéresse",
      to: "à",
      plus: "65+",
      physicalTitle: "Attirance physique",
      physicalSub: "Facultatif, faible pondération (10%). Nous aide à affiner vos matchs.",
      attributes: "À votre sujet",
      preferences: "Ce qui vous attire (choisissez librement)",
      preview: "Aperçu de mon profil",
      previewTitle: "Comment les autres vous verront",
      close: "Fermer",
      lookingForAges: "Ouvert aux âges {min}–{max}",
      dims: {
        height: { label: "Taille", options: { short: "Petit", average: "Moyenne", tall: "Grand" } },
        build: { label: "Corpulence", options: { slim: "Mince", athletic: "Athlétique", average: "Moyenne", curvy: "Pulpeuse", plus: "Grande taille" } },
        hair: { label: "Cheveux", options: { blonde: "Blonds", brown: "Bruns", black: "Noirs", red: "Roux", grey: "Gris", other: "Autre" } },
        style: { label: "Style", options: { casual: "Décontracté", classic: "Classique", edgy: "Audacieux", elegant: "Élégant", sporty: "Sportif" } },
      },
    },
  },

  pt: {
    verify: {
      title: "Verifique sua caixa de entrada",
      subtitle: "Digite o código de 6 dígitos que enviamos para {email} para verificar sua conta.",
      placeholder: "Código de 6 dígitos",
      verify: "Verificar",
      verifying: "Verificando...",
      resend: "Reenviar código",
      resendIn: "Reenviar em {s}s",
      resent: "Um novo código está a caminho 🤍",
      logout: "Usar outra conta",
    },
    onboarding: {
      title: "Vamos encontrar as suas pessoas",
      subtitle: "Doze perguntas honestas. Não há respostas certas — só você. É assim que o Reveal combina no que realmente importa.",
      start: "Começar",
      progress: "Pergunta {n} de {total}",
      next: "Continuar",
      back: "Voltar",
      finish: "Concluir",
      submitting: "Salvando...",
      textPlaceholder: "1–2 frases...",
      required: "Responda para continuar",
      categories: {
        values: { name: "Valores e visão de vida", tag: "Para onde você está construindo" },
        emotional: { name: "Estilo emocional", tag: "Como você ama, discute e se cura" },
        daily: { name: "Vida diária e energia", tag: "Compatibilidade de estilo de vida" },
        depth: { name: "Profundidade e autoconhecimento", tag: "O quanto você se conhece" },
      },
    },
    questions: {
      q1: { text: "Como é uma boa vida para você daqui a 10 anos?", options: ["Estabelecido em um lugar que chamo de lar, cercado por quem amo", "Ainda explorando — novos lugares, novos capítulos", "Construindo algo que me sobreviva (família, carreira, legado)", "Sinceramente, ainda estou descobrindo"] },
      q2: { text: "Quando o assunto é compromisso, você é:", options: ["Totalmente dentro a partir do momento em que escolho alguém", "Preciso de tempo para me sentir seguro antes de me abrir de verdade", "Amo profundamente, mas também preciso da minha independência", "Ainda estou aprendendo o que compromisso significa para mim"] },
      q3: { text: "Qual é o papel da família na sua vida?", options: ["Central — são a minha base", "Importante, mas também construí minha própria família escolhida", "Complicado — é algo em que estou trabalhando", "Estou focado em construir minha própria família agora"] },
      q4: { text: "Quando algo te magoa em um relacionamento, você tende a:", options: ["Dizer diretamente — prefiro falar a guardar", "Me afastar primeiro e voltar quando estiver pronto", "Minimizar para não haver conflito", "Escrever ou processar antes de conseguir falar"] },
      q5: { text: "Como você sabe que se sente seguro com alguém?", options: ["Consigo ficar em silêncio com a pessoa sem constrangimento", "Ela esteve presente quando não era conveniente", "Consigo dizer a verdade mais difícil e ela não vai embora", "Paro de atuar e simplesmente... existo"] },
      q6: { text: "Depois de uma discussão de verdade, você precisa de:", options: ["Espaço para respirar antes de reconectar", "Segurança imediata de que estamos bem", "Entender o que aconteceu antes de seguir em frente", "Proximidade física — um abraço resolve mais que palavras"] },
      q7: { text: "Seu domingo ideal é assim:", options: ["Manhãs lentas, boa comida, sem compromissos", "Ao ar livre ou fazendo algo ativo", "Cercado de amigos e pessoas que amo", "Criando algo — cozinhar, escrever, fazer"] },
      q8: { text: "Você recarrega as energias:", options: ["Ficando sozinho — preciso de silêncio para voltar a mim", "Com uma pessoa em quem confio completamente", "Cercado de gente — a energia me alimenta", "Depende da fase em que estou"] },
      q9: { text: "Quando o assunto é ambição, você é:", options: ["Muito determinado — meus objetivos são parte de quem eu sou", "Ambicioso, mas nunca deixo custar meus relacionamentos", "Valorizo mais a profundidade do que a conquista", "Estou numa fase de descobrir o que realmente quero"] },
      q10: { text: "O que a maioria não percebe em você de imediato é:" },
      q11: { text: "O que você acreditava sobre o amor e não acredita mais?" },
      q12: { text: "O que você realmente procura agora — seja honesto:", options: ["Um relacionamento real com um futuro real", "Conhecer alguém significativo, sem pressão", "Curar e crescer ao lado de alguém", "Ainda não sei — e tudo bem"] },
    },
    profileExtra: {
      ageRange: "Faixa etária que te interessa",
      to: "a",
      plus: "65+",
      physicalTitle: "Atração física",
      physicalSub: "Opcional, com pouco peso (10%). Ajuda a ajustar suas combinações.",
      attributes: "Sobre você",
      preferences: "Você se atrai por (escolha quantas quiser)",
      preview: "Pré-visualizar meu perfil",
      previewTitle: "Como os outros verão você",
      close: "Fechar",
      lookingForAges: "Aberto a idades {min}–{max}",
      dims: {
        height: { label: "Altura", options: { short: "Baixo", average: "Média", tall: "Alto" } },
        build: { label: "Físico", options: { slim: "Magro", athletic: "Atlético", average: "Médio", curvy: "Curvilíneo", plus: "Plus size" } },
        hair: { label: "Cabelo", options: { blonde: "Loiro", brown: "Castanho", black: "Preto", red: "Ruivo", grey: "Grisalho", other: "Outro" } },
        style: { label: "Estilo", options: { casual: "Casual", classic: "Clássico", edgy: "Ousado", elegant: "Elegante", sporty: "Esportivo" } },
      },
    },
  },

  it: {
    verify: {
      title: "Controlla la tua casella di posta",
      subtitle: "Inserisci il codice di 6 cifre inviato a {email} per verificare il tuo account.",
      placeholder: "Codice di 6 cifre",
      verify: "Verifica",
      verifying: "Verifica in corso...",
      resend: "Invia di nuovo il codice",
      resendIn: "Reinvia tra {s}s",
      resent: "Un nuovo codice è in arrivo 🤍",
      logout: "Usa un altro account",
    },
    onboarding: {
      title: "Troviamo le tue persone",
      subtitle: "Dodici domande sincere. Nessuna risposta giusta — solo te. È così che Reveal abbina su ciò che conta davvero.",
      start: "Inizia",
      progress: "Domanda {n} di {total}",
      next: "Continua",
      back: "Indietro",
      finish: "Concludi",
      submitting: "Salvataggio...",
      textPlaceholder: "1–2 frasi...",
      required: "Rispondi per continuare",
      categories: {
        values: { name: "Valori e visione di vita", tag: "Verso cosa stai costruendo" },
        emotional: { name: "Stile emotivo", tag: "Come ami, litighi e guarisci" },
        daily: { name: "Vita quotidiana ed energia", tag: "Compatibilità di stile di vita" },
        depth: { name: "Profondità e consapevolezza di sé", tag: "Quanto ti conosci" },
      },
    },
    questions: {
      q1: { text: "Come immagini una bella vita tra 10 anni?", options: ["Stabile in un posto che chiamo casa, con le persone che amo accanto", "Ancora in esplorazione — nuovi luoghi, nuovi capitoli", "Costruire qualcosa che mi sopravviva (famiglia, carriera, eredità)", "Onestamente, lo sto ancora capendo"] },
      q2: { text: "Quando si tratta di impegno, tu sei:", options: ["Dentro al 100% dal momento in cui scelgo qualcuno", "Ho bisogno di tempo per sentirmi al sicuro prima di aprirmi", "Amo profondamente ma ho bisogno anche della mia indipendenza", "Sto ancora imparando cosa significhi l'impegno per me"] },
      q3: { text: "Che ruolo ha la famiglia nella tua vita?", options: ["Centrale — sono le mie fondamenta", "Importante, ma ho creato anche la mia famiglia scelta", "Complicato — è qualcosa su cui sto lavorando", "Ora sono concentrato a costruire la mia famiglia"] },
      q4: { text: "Quando qualcosa ti ferisce in una relazione, tendi a:", options: ["Dirlo direttamente — preferisco parlare che rimuginare", "Ritirarmi prima, poi tornare quando sono pronto", "Minimizzarlo per evitare conflitti", "Scriverlo o elaborarlo prima di riuscire a parlarne"] },
      q5: { text: "Come capisci di sentirti al sicuro con qualcuno?", options: ["Posso stare in silenzio senza che sia imbarazzante", "C'è stato per me quando non era comodo", "Posso dire la verità più scomoda e non se ne va", "Smetto di recitare e semplicemente... esisto"] },
      q6: { text: "Dopo un vero litigio, hai bisogno di:", options: ["Spazio per respirare prima di riavvicinarmi", "Rassicurazione immediata che siamo a posto", "Capire cosa è successo prima di andare avanti", "Vicinanza fisica — un abbraccio vale più delle parole"] },
      q7: { text: "La tua domenica ideale è:", options: ["Mattinate lente, buon cibo, nessun impegno", "All'aria aperta o a fare qualcosa di attivo", "Circondato da amici e persone che amo", "Creare qualcosa — cucinare, scrivere, fare"] },
      q8: { text: "Recuperi energie:", options: ["Stando da solo — mi serve silenzio per ritrovarmi", "Con una persona di cui mi fido completamente", "Circondato dalle persone — l'energia mi nutre", "Dipende dal periodo che sto vivendo"] },
      q9: { text: "Quando si tratta di ambizione, tu sei:", options: ["Molto determinato — i miei obiettivi sono parte di chi sono", "Ambizioso ma non a costo delle mie relazioni", "Mi interessa più la profondità del successo", "Sto cercando di capire cosa voglio davvero"] },
      q10: { text: "La cosa che la maggior parte non nota subito di te è:" },
      q11: { text: "Cosa credevi sull'amore che ora non credi più?" },
      q12: { text: "Cosa cerchi davvero in questo momento — sii sincero:", options: ["Una relazione vera con un futuro vero", "Conoscere qualcuno di significativo, senza pressioni", "Guarire e crescere accanto a qualcuno", "Non lo so ancora — e va bene così"] },
    },
    profileExtra: {
      ageRange: "Fascia d'età che ti interessa",
      to: "a",
      plus: "65+",
      physicalTitle: "Attrazione fisica",
      physicalSub: "Facoltativo, con peso ridotto (10%). Ci aiuta a perfezionare i tuoi abbinamenti.",
      attributes: "Su di te",
      preferences: "Ti attrae (scegli liberamente)",
      preview: "Anteprima del mio profilo",
      previewTitle: "Come ti vedranno gli altri",
      close: "Chiudi",
      lookingForAges: "Aperto a età {min}–{max}",
      dims: {
        height: { label: "Altezza", options: { short: "Basso", average: "Media", tall: "Alto" } },
        build: { label: "Corporatura", options: { slim: "Snella", athletic: "Atletica", average: "Media", curvy: "Formosa", plus: "Taglia forte" } },
        hair: { label: "Capelli", options: { blonde: "Biondi", brown: "Castani", black: "Neri", red: "Rossi", grey: "Grigi", other: "Altro" } },
        style: { label: "Stile", options: { casual: "Casual", classic: "Classico", edgy: "Audace", elegant: "Elegante", sporty: "Sportivo" } },
      },
    },
  },

  de: {
    verify: {
      title: "Sieh in deinem Posteingang nach",
      subtitle: "Gib den 6-stelligen Code ein, den wir an {email} gesendet haben, um dein Konto zu bestätigen.",
      placeholder: "6-stelliger Code",
      verify: "Bestätigen",
      verifying: "Wird bestätigt...",
      resend: "Code erneut senden",
      resendIn: "Erneut in {s}s",
      resent: "Ein neuer Code ist unterwegs 🤍",
      logout: "Anderes Konto verwenden",
    },
    onboarding: {
      title: "Finden wir deine Menschen",
      subtitle: "Zwölf ehrliche Fragen. Keine richtigen Antworten — nur du. So bringt Reveal zusammen, was wirklich zählt.",
      start: "Beginnen",
      progress: "Frage {n} von {total}",
      next: "Weiter",
      back: "Zurück",
      finish: "Abschließen",
      submitting: "Wird gespeichert...",
      textPlaceholder: "1–2 Sätze...",
      required: "Bitte beantworten, um fortzufahren",
      categories: {
        values: { name: "Werte & Lebensvision", tag: "Worauf du hinarbeitest" },
        emotional: { name: "Emotionaler Stil", tag: "Wie du liebst, streitest und heilst" },
        daily: { name: "Alltag & Energie", tag: "Lebensstil-Kompatibilität" },
        depth: { name: "Tiefe & Selbstkenntnis", tag: "Wie gut du dich kennst" },
      },
    },
    questions: {
      q1: { text: "Wie sieht ein gutes Leben für dich in 10 Jahren aus?", options: ["Angekommen an einem Ort, den ich Zuhause nenne, mit geliebten Menschen um mich", "Immer noch am Entdecken — neue Orte, neue Kapitel", "Etwas aufbauen, das mich überdauert (Familie, Karriere, Vermächtnis)", "Ehrlich gesagt finde ich das gerade noch heraus"] },
      q2: { text: "Wenn es um Bindung geht, bist du:", options: ["Voll dabei, sobald ich mich für jemanden entscheide", "Ich brauche Zeit, um mich sicher zu fühlen, bevor ich mich öffne", "Ich liebe tief, brauche aber auch meine Unabhängigkeit", "Ich lerne noch, was Bindung für mich bedeutet"] },
      q3: { text: "Welche Rolle spielt Familie in deinem Leben?", options: ["Zentral — sie ist mein Fundament", "Wichtig, aber ich habe auch meine eigene Wahlfamilie", "Kompliziert — daran arbeite ich noch", "Ich konzentriere mich jetzt darauf, meine eigene Familie aufzubauen"] },
      q4: { text: "Wenn dich etwas in einer Beziehung verletzt, neigst du dazu:", options: ["Es direkt anzusprechen — lieber reden als schweigen", "Mich erst zurückzuziehen und zurückzukommen, wenn ich bereit bin", "Es kleinzureden, damit es keinen Konflikt gibt", "Es aufzuschreiben oder zu verarbeiten, bevor ich darüber sprechen kann"] },
      q5: { text: "Woran merkst du, dass du dich bei jemandem sicher fühlst?", options: ["Ich kann mit der Person still sein, ohne dass es unangenehm ist", "Sie war für mich da, als es unbequem war", "Ich kann die unbequeme Wahrheit sagen und sie bleibt", "Ich höre auf zu spielen und bin einfach... da"] },
      q6: { text: "Nach einem echten Streit brauchst du:", options: ["Raum zum Durchatmen, bevor wir uns wieder annähern", "Sofortige Bestätigung, dass alles okay ist", "Zu verstehen, was passiert ist, bevor ich weitermachen kann", "Körperliche Nähe — eine Umarmung heilt mehr als Worte"] },
      q7: { text: "Dein idealer Sonntag sieht so aus:", options: ["Langsame Morgen, gutes Essen, nichts zu tun", "Draußen in der Natur oder aktiv sein", "Umgeben von Freunden und geliebten Menschen", "Etwas erschaffen — kochen, schreiben, gestalten"] },
      q8: { text: "Du tankst auf, indem du:", options: ["Allein bist — ich brauche Ruhe, um zu mir zu finden", "Mit einer Person zusammen bist, der ich völlig vertraue", "Unter Menschen bist — Energie nährt mich", "Es hängt von der Lebensphase ab"] },
      q9: { text: "Wenn es um Ehrgeiz geht, bist du:", options: ["Sehr getrieben — meine Ziele sind ein Kernteil von mir", "Ehrgeizig, aber nie auf Kosten meiner Beziehungen", "Mir ist Tiefe wichtiger als Erfolg", "Ich finde gerade heraus, was ich wirklich will"] },
      q10: { text: "Was die meisten nicht sofort an dir sehen, ist:" },
      q11: { text: "Was hast du früher über die Liebe geglaubt, das du heute nicht mehr glaubst?" },
      q12: { text: "Wonach suchst du gerade wirklich — sei ehrlich:", options: ["Eine echte Beziehung mit echter Zukunft", "Jemanden Bedeutungsvollen kennenlernen, ohne Druck", "Heilen und gemeinsam wachsen", "Ich weiß es noch nicht — und das ist okay"] },
    },
    profileExtra: {
      ageRange: "Altersspanne, für die du offen bist",
      to: "bis",
      plus: "65+",
      physicalTitle: "Körperliche Anziehung",
      physicalSub: "Optional, gering gewichtet (10%). Hilft uns, deine Matches zu verfeinern.",
      attributes: "Über dich",
      preferences: "Dich zieht an (wähle beliebig)",
      preview: "Mein Profil ansehen",
      previewTitle: "So sehen dich andere",
      close: "Schließen",
      lookingForAges: "Offen für Alter {min}–{max}",
      dims: {
        height: { label: "Größe", options: { short: "Klein", average: "Mittel", tall: "Groß" } },
        build: { label: "Statur", options: { slim: "Schlank", athletic: "Athletisch", average: "Durchschnittlich", curvy: "Kurvig", plus: "Kräftig" } },
        hair: { label: "Haare", options: { blonde: "Blond", brown: "Braun", black: "Schwarz", red: "Rot", grey: "Grau", other: "Andere" } },
        style: { label: "Stil", options: { casual: "Leger", classic: "Klassisch", edgy: "Auffällig", elegant: "Elegant", sporty: "Sportlich" } },
      },
    },
  },

  nl: {
    verify: {
      title: "Controleer je inbox",
      subtitle: "Voer de 6-cijferige code in die we naar {email} hebben gestuurd om je account te verifiëren.",
      placeholder: "6-cijferige code",
      verify: "Verifiëren",
      verifying: "Verifiëren...",
      resend: "Code opnieuw sturen",
      resendIn: "Opnieuw over {s}s",
      resent: "Een nieuwe code is onderweg 🤍",
      logout: "Ander account gebruiken",
    },
    onboarding: {
      title: "Laten we jouw mensen vinden",
      subtitle: "Twaalf eerlijke vragen. Geen goede antwoorden — alleen jij. Zo matcht Reveal op wat er echt toe doet.",
      start: "Beginnen",
      progress: "Vraag {n} van {total}",
      next: "Doorgaan",
      back: "Terug",
      finish: "Afronden",
      submitting: "Opslaan...",
      textPlaceholder: "1–2 zinnen...",
      required: "Beantwoord om door te gaan",
      categories: {
        values: { name: "Waarden & levensvisie", tag: "Waar je naartoe bouwt" },
        emotional: { name: "Emotionele stijl", tag: "Hoe je liefhebt, ruziet en heelt" },
        daily: { name: "Dagelijks leven & energie", tag: "Levensstijl-compatibiliteit" },
        depth: { name: "Diepgang & zelfkennis", tag: "Hoe goed je jezelf kent" },
      },
    },
    questions: {
      q1: { text: "Hoe ziet een goed leven er voor jou uit over 10 jaar?", options: ["Gesetteld op een plek die ik thuis noem, met de mensen van wie ik hou", "Nog steeds aan het ontdekken — nieuwe plekken, nieuwe hoofdstukken", "Iets bouwen dat mij overstijgt (gezin, carrière, nalatenschap)", "Eerlijk gezegd zoek ik dat nog uit"] },
      q2: { text: "Als het om toewijding gaat, ben je:", options: ["Helemaal erin vanaf het moment dat ik voor iemand kies", "Ik heb tijd nodig om me veilig te voelen voordat ik me open", "Ik houd diep, maar heb ook mijn onafhankelijkheid nodig", "Ik leer nog wat toewijding voor mij betekent"] },
      q3: { text: "Welke rol speelt familie in je leven?", options: ["Centraal — zij zijn mijn basis", "Belangrijk, maar ik heb ook mijn eigen gekozen familie", "Ingewikkeld — daar werk ik aan", "Ik richt me nu op het bouwen van mijn eigen gezin"] },
      q4: { text: "Als iets je kwetst in een relatie, neig je ertoe om:", options: ["Het direct te zeggen — liever praten dan broeden", "Me eerst terug te trekken en terug te komen als ik klaar ben", "Het te minimaliseren zodat er geen conflict is", "Het op te schrijven of te verwerken voordat ik erover kan praten"] },
      q5: { text: "Hoe weet je dat je je veilig voelt bij iemand?", options: ["Ik kan stil zijn bij die persoon zonder ongemak", "Ze waren er voor me toen het niet uitkwam", "Ik kan de lelijke waarheid zeggen en ze gaan niet weg", "Ik stop met presteren en besta gewoon..."] },
      q6: { text: "Na een echte ruzie heb je nodig:", options: ["Ruimte om te ademen voordat we weer verbinden", "Onmiddellijke geruststelling dat het goed is", "Begrijpen wat er gebeurde voordat ik verder kan", "Fysieke nabijheid — een knuffel helpt meer dan woorden"] },
      q7: { text: "Jouw ideale zondag ziet er zo uit:", options: ["Trage ochtenden, lekker eten, nergens heen", "Buiten in de natuur of iets actiefs doen", "Omringd door vrienden en mensen van wie ik hou", "Iets creëren — koken, schrijven, maken"] },
      q8: { text: "Je laadt op door:", options: ["Alleen te zijn — ik heb stilte nodig om bij mezelf te komen", "Bij één persoon te zijn die ik volledig vertrouw", "Onder mensen te zijn — energie voedt me", "Het hangt af van de fase waarin ik zit"] },
      q9: { text: "Als het om ambitie gaat, ben je:", options: ["Sterk gedreven — mijn doelen zijn een kern van wie ik ben", "Ambitieus maar nooit ten koste van mijn relaties", "Ik hecht meer aan diepgang dan aan prestatie", "Ik ontdek nog wat ik echt wil"] },
      q10: { text: "Wat de meeste mensen niet meteen aan je zien is:" },
      q11: { text: "Wat geloofde je vroeger over liefde dat je nu niet meer gelooft?" },
      q12: { text: "Waar ben je nu echt naar op zoek — wees eerlijk:", options: ["Een echte relatie met een echte toekomst", "Iemand betekenisvols ontmoeten, zonder druk", "Helen en groeien naast iemand", "Ik weet het nog niet — en dat is oké"] },
    },
    profileExtra: {
      ageRange: "Leeftijdsbereik waarvoor je openstaat",
      to: "tot",
      plus: "65+",
      physicalTitle: "Fysieke aantrekking",
      physicalSub: "Optioneel, licht meegewogen (10%). Helpt ons je matches te verfijnen.",
      attributes: "Over jou",
      preferences: "Je valt op (kies er zoveel je wilt)",
      preview: "Mijn profiel bekijken",
      previewTitle: "Hoe anderen jou zien",
      close: "Sluiten",
      lookingForAges: "Open voor leeftijden {min}–{max}",
      dims: {
        height: { label: "Lengte", options: { short: "Klein", average: "Gemiddeld", tall: "Lang" } },
        build: { label: "Bouw", options: { slim: "Slank", athletic: "Atletisch", average: "Gemiddeld", curvy: "Curvy", plus: "Plus-size" } },
        hair: { label: "Haar", options: { blonde: "Blond", brown: "Bruin", black: "Zwart", red: "Rood", grey: "Grijs", other: "Anders" } },
        style: { label: "Stijl", options: { casual: "Casual", classic: "Klassiek", edgy: "Gewaagd", elegant: "Elegant", sporty: "Sportief" } },
      },
    },
  },
};

export const getAppStrings = (lang) => appStrings[lang] || appStrings.en;

// Signup profile-setup wizard + location strings (7 languages).
export const setupStrings = {
  en: {
    next: "Continue", back: "Back", finish: "Finish", submitting: "Saving...", skip: "Skip for now",
    banner: "Complete your profile to start matching 🤍", completeProfile: "Complete profile",
    distanceAway: "{d} km away",
    genderLabels: { Woman: "Woman", Man: "Man", "Non-binary": "Non-binary", Other: "Other" },
    lookingLabels: { Women: "Women", Men: "Men", Everyone: "Everyone" },
    steps: {
      photo: { title: "Add your photo", sub: "It stays blurred until your connection deepens.", add: "Add photo", uploading: "Uploading..." },
      details: { title: "Your details", sub: "The basics, so people know who they're meeting.", name: "Display name", age: "Age" },
      location: { title: "Where are you?", sub: "We use this to find people near you.", detect: "Detect my location", detecting: "Locating...", denied: "Location off — type your city instead.", city: "City", country: "Country", cityPlaceholder: "e.g. Lisbon" },
      gender: { title: "I am a", sub: "How you identify." },
      lookingFor: { title: "Looking for", sub: "Who you'd like to meet." },
      ageRange: { title: "Age range you're open to", sub: "" },
      locationPref: { title: "How far are you willing to match?", sub: "You can change this anytime." },
      bio: { title: "About you", sub: "Share something worth discovering.", placeholder: "A few honest lines..." },
    },
    locationPrefOptions: {
      my_town: { label: "My Town", desc: "within 25 km" },
      my_region: { label: "My Region", desc: "within 100 km" },
      my_country: { label: "My Country", desc: "same country only" },
      europe: { label: "Europe", desc: "European countries only" },
      worldwide: { label: "Worldwide", desc: "no distance limit" },
    },
  },
  es: {
    next: "Continuar", back: "Atrás", finish: "Finalizar", submitting: "Guardando...", skip: "Omitir por ahora",
    banner: "Completa tu perfil para empezar a conectar 🤍", completeProfile: "Completar perfil",
    distanceAway: "a {d} km",
    genderLabels: { Woman: "Mujer", Man: "Hombre", "Non-binary": "No binario", Other: "Otro" },
    lookingLabels: { Women: "Mujeres", Men: "Hombres", Everyone: "Todos" },
    steps: {
      photo: { title: "Añade tu foto", sub: "Permanece difuminada hasta que la conexión se profundice.", add: "Añadir foto", uploading: "Subiendo..." },
      details: { title: "Tus datos", sub: "Lo básico, para saber a quién se conoce.", name: "Nombre visible", age: "Edad" },
      location: { title: "¿Dónde estás?", sub: "Lo usamos para encontrar personas cerca de ti.", detect: "Detectar mi ubicación", detecting: "Localizando...", denied: "Ubicación desactivada: escribe tu ciudad.", city: "Ciudad", country: "País", cityPlaceholder: "p. ej. Lisboa" },
      gender: { title: "Soy", sub: "Cómo te identificas." },
      lookingFor: { title: "Busco", sub: "A quién te gustaría conocer." },
      ageRange: { title: "Rango de edad que te interesa", sub: "" },
      locationPref: { title: "¿Hasta dónde quieres conectar?", sub: "Puedes cambiarlo cuando quieras." },
      bio: { title: "Sobre ti", sub: "Comparte algo que valga la pena descubrir.", placeholder: "Unas líneas honestas..." },
    },
    locationPrefOptions: {
      my_town: { label: "Mi ciudad", desc: "hasta 25 km" },
      my_region: { label: "Mi región", desc: "hasta 100 km" },
      my_country: { label: "Mi país", desc: "solo mismo país" },
      europe: { label: "Europa", desc: "solo países europeos" },
      worldwide: { label: "Todo el mundo", desc: "sin límite de distancia" },
    },
  },
  fr: {
    next: "Continuer", back: "Retour", finish: "Terminer", submitting: "Enregistrement...", skip: "Ignorer pour l'instant",
    banner: "Complétez votre profil pour commencer à matcher 🤍", completeProfile: "Compléter le profil",
    distanceAway: "à {d} km",
    genderLabels: { Woman: "Femme", Man: "Homme", "Non-binary": "Non-binaire", Other: "Autre" },
    lookingLabels: { Women: "Femmes", Men: "Hommes", Everyone: "Tout le monde" },
    steps: {
      photo: { title: "Ajoutez votre photo", sub: "Elle reste floue jusqu'à ce que la connexion s'approfondisse.", add: "Ajouter une photo", uploading: "Téléversement..." },
      details: { title: "Vos infos", sub: "L'essentiel, pour savoir qui l'on rencontre.", name: "Nom affiché", age: "Âge" },
      location: { title: "Où êtes-vous ?", sub: "Nous l'utilisons pour trouver des gens près de vous.", detect: "Détecter ma position", detecting: "Localisation...", denied: "Localisation désactivée — saisissez votre ville.", city: "Ville", country: "Pays", cityPlaceholder: "ex. Lisbonne" },
      gender: { title: "Je suis", sub: "Comment vous vous identifiez." },
      lookingFor: { title: "Je cherche", sub: "Qui vous aimeriez rencontrer." },
      ageRange: { title: "Tranche d'âge qui vous intéresse", sub: "" },
      locationPref: { title: "Jusqu'où voulez-vous matcher ?", sub: "Modifiable à tout moment." },
      bio: { title: "À votre sujet", sub: "Partagez quelque chose à découvrir.", placeholder: "Quelques lignes sincères..." },
    },
    locationPrefOptions: {
      my_town: { label: "Ma ville", desc: "dans un rayon de 25 km" },
      my_region: { label: "Ma région", desc: "dans un rayon de 100 km" },
      my_country: { label: "Mon pays", desc: "même pays uniquement" },
      europe: { label: "Europe", desc: "pays européens uniquement" },
      worldwide: { label: "Monde entier", desc: "sans limite de distance" },
    },
  },
  pt: {
    next: "Continuar", back: "Voltar", finish: "Concluir", submitting: "Salvando...", skip: "Pular por agora",
    banner: "Complete seu perfil para começar a combinar 🤍", completeProfile: "Completar perfil",
    distanceAway: "a {d} km",
    genderLabels: { Woman: "Mulher", Man: "Homem", "Non-binary": "Não binário", Other: "Outro" },
    lookingLabels: { Women: "Mulheres", Men: "Homens", Everyone: "Todos" },
    steps: {
      photo: { title: "Adicione sua foto", sub: "Fica desfocada até a conexão se aprofundar.", add: "Adicionar foto", uploading: "Enviando..." },
      details: { title: "Seus dados", sub: "O básico, para saber quem se está conhecendo.", name: "Nome de exibição", age: "Idade" },
      location: { title: "Onde você está?", sub: "Usamos isso para encontrar pessoas perto de você.", detect: "Detectar minha localização", detecting: "Localizando...", denied: "Localização desativada — digite sua cidade.", city: "Cidade", country: "País", cityPlaceholder: "ex. Lisboa" },
      gender: { title: "Eu sou", sub: "Como você se identifica." },
      lookingFor: { title: "Procuro", sub: "Quem você gostaria de conhecer." },
      ageRange: { title: "Faixa etária que te interessa", sub: "" },
      locationPref: { title: "Até onde você quer combinar?", sub: "Pode mudar quando quiser." },
      bio: { title: "Sobre você", sub: "Compartilhe algo que valha a pena descobrir.", placeholder: "Algumas linhas honestas..." },
    },
    locationPrefOptions: {
      my_town: { label: "Minha cidade", desc: "até 25 km" },
      my_region: { label: "Minha região", desc: "até 100 km" },
      my_country: { label: "Meu país", desc: "somente mesmo país" },
      europe: { label: "Europa", desc: "somente países europeus" },
      worldwide: { label: "Mundo todo", desc: "sem limite de distância" },
    },
  },
  it: {
    next: "Continua", back: "Indietro", finish: "Concludi", submitting: "Salvataggio...", skip: "Salta per ora",
    banner: "Completa il profilo per iniziare ad abbinarti 🤍", completeProfile: "Completa il profilo",
    distanceAway: "a {d} km",
    genderLabels: { Woman: "Donna", Man: "Uomo", "Non-binary": "Non binario", Other: "Altro" },
    lookingLabels: { Women: "Donne", Men: "Uomini", Everyone: "Tutti" },
    steps: {
      photo: { title: "Aggiungi la tua foto", sub: "Resta sfocata finché la connessione non si approfondisce.", add: "Aggiungi foto", uploading: "Caricamento..." },
      details: { title: "I tuoi dati", sub: "L'essenziale, per sapere chi si incontra.", name: "Nome visualizzato", age: "Età" },
      location: { title: "Dove sei?", sub: "Lo usiamo per trovare persone vicino a te.", detect: "Rileva la mia posizione", detecting: "Localizzazione...", denied: "Posizione disattivata — scrivi la tua città.", city: "Città", country: "Paese", cityPlaceholder: "es. Lisbona" },
      gender: { title: "Sono", sub: "Come ti identifichi." },
      lookingFor: { title: "Cerco", sub: "Chi vorresti conoscere." },
      ageRange: { title: "Fascia d'età che ti interessa", sub: "" },
      locationPref: { title: "Fin dove vuoi abbinarti?", sub: "Puoi cambiarlo quando vuoi." },
      bio: { title: "Su di te", sub: "Condividi qualcosa che valga la pena scoprire.", placeholder: "Qualche riga sincera..." },
    },
    locationPrefOptions: {
      my_town: { label: "La mia città", desc: "entro 25 km" },
      my_region: { label: "La mia regione", desc: "entro 100 km" },
      my_country: { label: "Il mio paese", desc: "solo stesso paese" },
      europe: { label: "Europa", desc: "solo paesi europei" },
      worldwide: { label: "Tutto il mondo", desc: "nessun limite di distanza" },
    },
  },
  de: {
    next: "Weiter", back: "Zurück", finish: "Abschließen", submitting: "Wird gespeichert...", skip: "Vorerst überspringen",
    banner: "Vervollständige dein Profil, um zu matchen 🤍", completeProfile: "Profil vervollständigen",
    distanceAway: "{d} km entfernt",
    genderLabels: { Woman: "Frau", Man: "Mann", "Non-binary": "Nichtbinär", Other: "Andere" },
    lookingLabels: { Women: "Frauen", Men: "Männer", Everyone: "Alle" },
    steps: {
      photo: { title: "Füge dein Foto hinzu", sub: "Es bleibt unscharf, bis eure Verbindung wächst.", add: "Foto hinzufügen", uploading: "Wird hochgeladen..." },
      details: { title: "Deine Angaben", sub: "Das Wichtigste, damit man weiß, wen man trifft.", name: "Anzeigename", age: "Alter" },
      location: { title: "Wo bist du?", sub: "Wir nutzen das, um Menschen in deiner Nähe zu finden.", detect: "Meinen Standort erkennen", detecting: "Wird geortet...", denied: "Standort aus — gib deine Stadt ein.", city: "Stadt", country: "Land", cityPlaceholder: "z. B. Lissabon" },
      gender: { title: "Ich bin", sub: "Wie du dich identifizierst." },
      lookingFor: { title: "Ich suche", sub: "Wen du kennenlernen möchtest." },
      ageRange: { title: "Altersspanne, für die du offen bist", sub: "" },
      locationPref: { title: "Wie weit möchtest du matchen?", sub: "Jederzeit änderbar." },
      bio: { title: "Über dich", sub: "Teile etwas, das es zu entdecken lohnt.", placeholder: "Ein paar ehrliche Zeilen..." },
    },
    locationPrefOptions: {
      my_town: { label: "Meine Stadt", desc: "im Umkreis von 25 km" },
      my_region: { label: "Meine Region", desc: "im Umkreis von 100 km" },
      my_country: { label: "Mein Land", desc: "nur gleiches Land" },
      europe: { label: "Europa", desc: "nur europäische Länder" },
      worldwide: { label: "Weltweit", desc: "keine Entfernungsgrenze" },
    },
  },
  nl: {
    next: "Doorgaan", back: "Terug", finish: "Afronden", submitting: "Opslaan...", skip: "Nu overslaan",
    banner: "Maak je profiel compleet om te matchen 🤍", completeProfile: "Profiel voltooien",
    distanceAway: "{d} km verderop",
    genderLabels: { Woman: "Vrouw", Man: "Man", "Non-binary": "Non-binair", Other: "Anders" },
    lookingLabels: { Women: "Vrouwen", Men: "Mannen", Everyone: "Iedereen" },
    steps: {
      photo: { title: "Voeg je foto toe", sub: "Blijft wazig tot jullie verbinding groeit.", add: "Foto toevoegen", uploading: "Uploaden..." },
      details: { title: "Jouw gegevens", sub: "De basis, zodat men weet wie men ontmoet.", name: "Weergavenaam", age: "Leeftijd" },
      location: { title: "Waar ben je?", sub: "We gebruiken dit om mensen bij jou in de buurt te vinden.", detect: "Detecteer mijn locatie", detecting: "Lokaliseren...", denied: "Locatie uit — typ je stad in.", city: "Stad", country: "Land", cityPlaceholder: "bijv. Lissabon" },
      gender: { title: "Ik ben een", sub: "Hoe je jezelf identificeert." },
      lookingFor: { title: "Ik zoek", sub: "Wie je zou willen ontmoeten." },
      ageRange: { title: "Leeftijdsbereik waarvoor je openstaat", sub: "" },
      locationPref: { title: "Hoe ver wil je matchen?", sub: "Altijd aanpasbaar." },
      bio: { title: "Over jou", sub: "Deel iets dat de moeite waard is om te ontdekken.", placeholder: "Een paar eerlijke zinnen..." },
    },
    locationPrefOptions: {
      my_town: { label: "Mijn stad", desc: "binnen 25 km" },
      my_region: { label: "Mijn regio", desc: "binnen 100 km" },
      my_country: { label: "Mijn land", desc: "alleen zelfde land" },
      europe: { label: "Europa", desc: "alleen Europese landen" },
      worldwide: { label: "Wereldwijd", desc: "geen afstandslimiet" },
    },
  },
};

export const getSetup = (lang) => setupStrings[lang] || setupStrings.en;
export const LOCATION_PREFS = ["my_town", "my_region", "my_country", "europe", "worldwide"];
export const GENDER_VALUES = ["Woman", "Man", "Non-binary", "Other"];
export const LOOKING_VALUES = ["Women", "Men", "Everyone"];
